<template>
    <component :is="tag">
        <rkm-row
            v-if="!hideFilters"
            class="mb-2 flex-nowrap"
        >
            <rkm-col
                :cols="!hideSortOptions && hasSortOptions ? '6' : '12'"
            >
                <rkm-form-input
                    placeholder="Digite para buscar"
                    v-model="localFieldFilter"
                    @keyup.native.enter="handleEnterKey"
                >
                    <rkm-input-group-prepend slot="prepend">
                        <rkm-form-select
                            :options="searchOptions"
                            class="m-0"
                            v-model="localSearchOption"
                        />
                    </rkm-input-group-prepend>
                </rkm-form-input>
            </rkm-col>

            <template v-if="!hideSortOptions && hasSortOptions">
                <rkm-col
                    class="flex-grow-1"
                >
                    <rkm-form-group
                        label-cols="auto"
                        label="Ordenar"
                        class="m-0"
                    >
                        <rkm-form-select
                            :options="sortOptions"
                            v-model="sortBy"
                            show-default-option
                            class="m-0"
                        >
                            <template #append>
                                <rkm-form-select
                                    :disabled="!sortBy"
                                    v-model="sortDesc"
                                    class="m-0"
                                >
                                    <rkm-form-select-option :value="false">Asc</rkm-form-select-option>
                                    <rkm-form-select-option :value="true">Desc</rkm-form-select-option>
                                </rkm-form-select>
                            </template>
                        </rkm-form-select>
                    </rkm-form-group>
                </rkm-col>
            </template>

            <template>
                <rkm-col cols="auto">
                    <rkm-button
                        :disabled="!localFieldFilter"
                        @click="search"
                        color="primary"
                        class="btn-search"
                    >
                        Buscar
                    </rkm-button>
                    <rkm-button
                        :disabled="!localFieldFilter"
                        @click="clear"
                        class="btn-clear"
                    >
                        Limpar
                    </rkm-button>
                </rkm-col>
            </template>
        </rkm-row>

        <rkm-row>
            <rkm-col>
                <rkm-list-group>
                    <template v-if="busy">
                        <rkm-list-group-item class="text-center">
                            <rkm-row>
                                <rkm-col>
                                    <b-spinner
                                        label="Carregando..."
                                        small
                                        variant="primary"
                                    />
                                </rkm-col>
                            </rkm-row>
                        </rkm-list-group-item>
                    </template>

                    <template v-else>
                        <template v-if="internalItems.length > 0">
                            <div
                                v-for="(item, i) in (hidePagination || repository) ? internalItems : internalItems.slice((context.currentPage - 1) * context.perPage, context.currentPage * context.perPage)"
                                v-bind:key="i"
                                :class="i === internalItems.length - 1 ? '' : 'mb-1'"
                            >
                                <rkm-list-group-item
                                    class="rkm-list-group-item"
                                    v-rkm-toggle="$scopedSlots.collapse ? uniqueId(`rkm-collapse-${i}`) : null"
                                    :style="`cursor: ${itemCursor}`"
                                    :color="item._listItemColor || 'none'"
                                >
                                    <rkm-row class="align-items-center">
                                        <rkm-col
                                            v-if="$scopedSlots.leading"
                                            cols="auto"
                                        >
                                            <slot
                                                name="leading"
                                                v-bind:item="item"
                                                v-bind:index="i"
                                            />
                                        </rkm-col>

                                        <rkm-col class="flex-grow-1">
                                            <slot v-bind:item="item" v-bind:index="i" />
                                        </rkm-col>

                                        <rkm-col
                                            v-if="$scopedSlots.trailling"
                                            cols="auto"
                                        >
                                            <slot
                                                name="trailling"
                                                v-bind:item="item"
                                            />
                                        </rkm-col>
                                    </rkm-row>
                                </rkm-list-group-item>

                                <rkm-collapse
                                    v-if="$scopedSlots.collapse"
                                    :id="uniqueId(`rkm-collapse-${i}`)"
                                    :visible="item.collapse"
                                    :accordion="accordion ? uniqueId('rkm-collapse') : null"
                                    @hide="$emit('collapse-hide', item)"
                                    @show="$emit('collapse-show', item)"
                                    class="rkm-collapse"
                                >
                                    <slot
                                        name="collapse"
                                        v-bind:item="item"
                                        v-bind:index="i"
                                    />
                                </rkm-collapse>
                            </div>
                        </template>

                        <template v-else>
                            <rkm-list-group-item class="text-center">
                                <rkm-row>
                                    <rkm-col>
                                        Não há registros correspondentes à sua solicitação
                                    </rkm-col>
                                </rkm-row>
                            </rkm-list-group-item>
                        </template>
                    </template>
                </rkm-list-group>
            </rkm-col>
        </rkm-row>

        <rkm-row
            v-if="!hidePagination"
            class="mt-2"
        >
            <rkm-col>
                <rkm-row class="align-items-center">
                    <rkm-col cols="auto">
                        <b-pagination
                            :per-page="context.perPage"
                            :total-rows="recordsFiltered"
                            v-model="context.currentPage"
                        />
                    </rkm-col>

                    <rkm-col
                        v-if="!busy"
                        cols="auto"
                    >
                        <template v-if="recordsFiltered > 0">
                            Exibindo do {{ ((context.currentPage - 1) * context.perPage) + 1 }}
                            ao {{ context.currentPage * context.perPage }}
                            de {{ recordsFiltered }} registro(s)
                        </template>

                        <template v-if="recordsTotal">
                            (Filtrado de {{ recordsTotal }} registros totais)
                        </template>
                    </rkm-col>
                </rkm-row>
            </rkm-col>

            <rkm-col
                cols="auto"
            >
                <rkm-form-group
                    label-align="left"
                    label-cols="auto"
                    label="Por página"
                    class="m-0"
                >
                    <rkm-form-select
                        :options="pageOptions"
                        v-model="context.perPage"
                        class="m-0"
                    />
                </rkm-form-group>
            </rkm-col>
        </rkm-row>
    </component>
</template>

<script>
// Mixins.
import { error, repository, data, stringCase } from '@/mixins'

import { BSpinner, BPagination } from 'bootstrap-vue'

export default {
    components: { BSpinner, BPagination },
    computed: {
        sortOptions() {
            return this.fields.filter(field => !!field.sortable).map(field => {
                return { text: field.label, value: field.key }
            })
        },
        hasSortOptions() {
            return this.sortOptions.length > 0
        },
        searchOptions() {
            return this.fields.filter(field => !!field.searchable).map(field => {
                return { text: field.label, value: field.key }
            })
        },
        localFields() {
            return this.fields.filter(f => (typeof (f.visible) === 'boolean' ? f.visible : true) === true)
        },
    },
    data: () => ({
        busy: false,
        context: {
            currentPage: 1,
            filter: '',
            perPage: 10,
        },
        internalItems: [],
        pageOptions: [10, 50, 100],
        recordsFiltered: 0,
        recordsTotal: 0,
        localFieldFilter: null,
        localFilter: null,
        localSearchOption: null,
        localSortBy: null,
        localSortDesc: false,
        sortBy: null,
        sortDesc: false,
    }),
    methods: {
        async datatable() {
            if (this.isNullOrEmptyString(this.context.filter)) {
                this.internalItems = []
                this.recordsFiltered = 0
                this.recordsTotal = 0
            } else {
                try {
                    this.busy = true

                    const params = {
                        draw: this.context.filter.length,
                        search: { value: this.context.filter },
                        columns: this.fields.filter(column => column.searchable).map(column => ({
                            name: this.camelToSnake(column.key),
                            orderable: !!column.sortable,
                            search: { value: column.search || '' },
                            searchable: column.key === this.localSearchOption,
                        })),
                        start: (this.context.currentPage - 1) * this.context.perPage,
                        length: this.context.perPage,
                        with_paciente_bloqueado: true,
                        with_paciente_provisorio: true,
                    }

                    if (this.sortBy) {
                        let order = [{
                            column: this.fields.findIndex(field => field.key == this.sortBy),
                            dir: this.sortDesc ? 'desc' : 'asc',
                        }]

                        Object.assign(params, { order })
                    }

                    await this.repository.datatable({ ...params })
                        .then(response => {
                            this.internalItems = response.data.data
                            this.recordsFiltered = response.data.recordsFiltered
                            this.recordsTotal = response.data.recordsTotal
                        })
                } catch (error) {
                    this.handleError(error)
                } finally {
                    this.busy = false
                }
            }
        },
        search() {
            this.context.filter = this.localFieldFilter
        },
        clear() {
            this.localSearchOption = this.searchBy || this.searchOptions[0].value;
            this.sortBy = null
            this.sortDesc = false
            this.clearSearchOption()
        },
        clearSearchOption() {
            this.localFieldFilter = null
            this.clearTable()
        },
        clearTable() {
            this.context.filter = null
            this.recordsFiltered = 0
            this.recordsTotal = 0
        },
        handleEnterKey(event) {

            if (event.key === 'Enter' && this.localFieldFilter) {
                this.search();
            }
        },
    },
    mixins: [ error, repository, data, stringCase ],
    mounted() {

        this.internalItems = this.items

        if (this.searchOptions.length > 0) {
            this.localSearchOption = this.searchBy || this.searchOptions[0].value
        }
    },
    props: {
        accordion: { type: Boolean, default: true },
        fields: { type: Array, default: () => [] },
        hideFilters: { type: Boolean, default: false },
        hidePagination: { type: Boolean, default: false },
        hideSortOptions: { type: Boolean, default: false },
        itemCursor: { type: String, default: 'pointer' },
        items: { type: Array, default: () => [] },
        repository: { type: Object },
        searchBy: { type: String, default: null },
        tag: { type: String, default: 'div' },
    },
    watch: {
        context: {
            deep: true,
            handler(value) {
                if (this.repository) {
                    this.datatable()
                } else {
                    this.internalItems = this.items
                        .filter(item => {
                            for (let key in item) {
                                if (key === 'collapse')
                                    return false

                                if (item[key].toUpperCase().includes(value.filter.toUpperCase()))
                                    return true
                            }

                            return false
                        })
                        .sort((a, b) => {
                            if (this.sortDesc) {
                                if (a[this.sortBy] < b[this.sortBy]) {
                                    return 1
                                }
                            } else {
                                if (a[this.sortBy] > b[this.sortBy]) {
                                    return 1
                                }
                            }

                            return -1
                        })
                }
            },
        },
        internalItems(value) {
            if (!this.repository) {
                this.recordsFiltered = value.length
            }
        },
        items: {
            deep: true,
            handler(value) {
                this.internalItems = value
            }
        },
        'localSearchOption': function () {

            this.clearSearchOption()
        },
        'localFieldFilter': function () {

            this.clearTable()
        },
        'searchBy': function () {

            if (this.searchOptions.length > 0) {
                this.localSearchOption = this.searchBy
            }
        },
        'sortBy': function () {

            this.clearTable()
        },
        'sortDesc': function () {

            this.clearTable()
        },
    }
}
</script>

<style scoped>
.rkm-list-group-item {
    padding: 0.75rem 1.25rem;
}
.rkm-list-group-item:focus {
    outline: none;
}
.rkm-list-group-item:first-child {
    border-radius: 0;
}
.rkm-list-group-item:last-child {
    border-radius: 0;
}
.rkm-list-group-item > .row {
    margin: 0;
}
.rkm-collapse {
    border-left: 1px solid rgba(0, 0, 0, .15);
    border-right: 1px solid rgba(0, 0, 0, .15);
    border-bottom: 1px solid rgba(0, 0, 0, .15);
}
.btn-search {
    border-top-right-radius: 0.25rem!important;
    border-bottom-right-radius: 0.25rem!important;
}
.btn-clear {
    border-top-left-radius: 0.25rem!important;
    border-bottom-left-radius: 0.25rem!important;
}
</style>